/**
 * Scanner QR à REPLI PROGRESSIF (§16.2, §25.2).
 *
 * Défaut corrigé : le bouton disparaissait purement et simplement quand
 * `BarcodeDetector` était absent (Firefox, iOS Safari, desktop…). Le technicien
 * n'avait alors AUCUN moyen d'ouvrir une machine par son QR.
 *
 * Chaîne de repli — l'utilisateur a toujours un chemin :
 *   1. scanner NATIF (Capacitor + ML Kit) sur téléphone installé ;
 *   2. scanner WEB avec caméra et BarcodeDetector, sinon décodage logiciel jsQR ;
 *   3. IMPORT d'une image contenant le QR (décodée par BarcodeDetector, sinon jsQR) ;
 *   4. SAISIE MANUELLE du code — toujours disponible, jamais masquée.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../core/api.js';
import { cachedEquipmentIdByQr } from '../offline/cached-records.js';
import { Camera, Image as ImageIcon, Keyboard } from 'lucide-react';
import { attachCamera, cameraErrorMessage } from '../core/live-camera.js';
import { t } from '../core/i18n.js';
import { Button, Field, Input, Modal } from './ui.js';
import { Banner } from './ui/Feedback.js';

/** Extrait le jeton QR d'une valeur scannée (URL complète ou jeton nu). */
export function extractQrToken(value: string): string | undefined {
  const trimmed = value.trim();
  const fromUrl = /\/qr\/([A-Za-z0-9_-]{16,})/.exec(trimmed);
  if (fromUrl) return fromUrl[1];
  // Jeton collé seul (ce que voit l'utilisateur sous l'étiquette)
  if (/^[A-Za-z0-9_-]{16,}$/.test(trimmed)) return trimmed;
  return undefined;
}

/** Application native (Capacitor) : un vrai scanner système est disponible. */
async function scanNative(): Promise<string | undefined> {
  const { Capacitor } = await import('@capacitor/core');
  if (!Capacitor.isNativePlatform()) return undefined;
  const { BarcodeScanner, BarcodeFormat } = await import('@capacitor-mlkit/barcode-scanning');
  const permission = await BarcodeScanner.requestPermissions();
  if (permission.camera !== 'granted' && permission.camera !== 'limited') return undefined;
  const { barcodes } = await BarcodeScanner.scan({ formats: [BarcodeFormat.QrCode] });
  return barcodes[0]?.rawValue;
}

/** Décode un QR depuis une image : BarcodeDetector si présent, sinon jsQR. */
async function decodeImage(file: File): Promise<string | undefined> {
  const bitmap = await createImageBitmap(file);
  if ('BarcodeDetector' in globalThis) {
    const Detector = (globalThis as unknown as {
      BarcodeDetector: new (o: { formats: string[] }) => {
        detect: (s: ImageBitmap) => Promise<{ rawValue: string }[]>;
      };
    }).BarcodeDetector;
    const codes = await new Detector({ formats: ['qr_code'] }).detect(bitmap);
    if (codes[0]) return codes[0].rawValue;
  }
  // Repli universel : décodage logiciel (chargé à la demande, hors bundle principal)
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return undefined;
  ctx.drawImage(bitmap, 0, 0);
  const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const jsQR = (await import('jsqr')).default;
  return jsQR(data, width, height)?.data;
}

/**
 * Le scanner et sa fenêtre. `open` le lance depuis n'importe quel geste
 * (« Créer », recherche) ; `dialog` se pose une fois dans la coque, pour
 * survivre à la feuille qui l'a ouvert.
 */
export function useQrScanner(): { open: () => void; dialog: ReactNode } {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState<string | undefined>();
  const [manual, setManual] = useState('');
  const [cameraOn, setCameraOn] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream>(undefined);

  const runRef = useRef(0);
  useEffect(() => () => {
    runRef.current += 1;
    streamRef.current?.getTracks().forEach((track) => track.stop());
  }, []);

  const stopCamera = (): void => {
    runRef.current += 1;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = undefined;
    setCameraOn(false);
  };

  const close = (): void => {
    stopCamera();
    setOpen(false);
    setInfo(undefined);
    setManual('');
  };

  const accept = (value: string | undefined): void => {
    const token = value ? extractQrToken(value) : undefined;
    if (!token) {
      setInfo(t('qr.qrIntrouvableDansImage'));
      return;
    }
    close();
    // Connecté : la fiche interne de la machine (et ses voisines du site) quand
    // elle appartient à l'entreprise ; sinon la page publique du QR.
    // Sans réseau : la machine est cherchée parmi celles de l'appareil (WEB1-08).
    void api<{ id: string }>(`/equipments/by-qr/${encodeURIComponent(token)}`)
      .then((found) => found.id)
      .catch(async (error: unknown) => (
        error instanceof ApiError && error.code === 'network_error' ? cachedEquipmentIdByQr(token) : undefined))
      .then((id) => navigate(id ? `/equipements/${id}` : `/qr/${token}`));
  };

  /** Ouvre le scanner : natif si possible, sinon caméra web, sinon on reste
   *  sur les replis (import d'image / saisie manuelle) déjà affichés. */
  const openScanner = async (): Promise<void> => {
    stopCamera();
    const run = runRef.current;
    setInfo(undefined);
    setOpen(true);
    try {
      const native = await scanNative();
      if (runRef.current !== run) return;
      if (native) {
        accept(native);
        return;
      }
    } catch {
      // plugin natif indisponible : on continue vers le scanner web
    }
    if (runRef.current !== run) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      setInfo(cameraErrorMessage());
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      if (runRef.current !== run) { stream.getTracks().forEach((track) => track.stop()); return; }
      streamRef.current = stream;
      setCameraOn(true);
      const video = await attachCamera(stream, () => videoRef.current,
        () => runRef.current === run && streamRef.current === stream);
      if (!video) return;
      const Detector = (globalThis as unknown as {
        BarcodeDetector?: {
          new (o: { formats: string[] }): { detect: (s: HTMLVideoElement) => Promise<{ rawValue: string }[]> };
          getSupportedFormats?: () => Promise<string[]>;
        };
      }).BarcodeDetector;
      let detector: { detect: (s: HTMLVideoElement) => Promise<{ rawValue: string }[]> } | undefined;
      try {
        if (Detector && (await Detector.getSupportedFormats?.())?.includes('qr_code')) detector = new Detector({ formats: ['qr_code'] });
      } catch { /* Use the software fallback for unsupported native detection. */ }
      const jsQR = (await import('jsqr')).default;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const tick = async (): Promise<void> => {
        if (runRef.current !== run || !streamRef.current) return;
        try {
          let codes: { rawValue: string }[] = [];
          if (detector) {
            try { codes = await detector.detect(video); } catch { detector = undefined; }
          }
          if (!detector && ctx && video.videoWidth) {
            canvas.width = Math.min(video.videoWidth, 960);
            canvas.height = Math.round(video.videoHeight * canvas.width / video.videoWidth);
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const result = jsQR(frame.data, frame.width, frame.height);
            if (result) codes = [{ rawValue: result.data }];
          }
          if (runRef.current !== run || !streamRef.current) return;
          if (codes[0]) {
            accept(codes[0].rawValue);
            return;
          }
        } catch {
          // image non décodable : on continue
        }
        window.setTimeout(() => void tick(), 150);
      };
      void tick();
    } catch (error) {
      if (runRef.current !== run) return;
      stopCamera();
      setInfo(cameraErrorMessage(error));
    }
  };

  const importImage = async (file: File): Promise<void> => {
    setInfo(undefined);
    try {
      accept(await decodeImage(file));
    } catch {
      setInfo(t('qr.qrIntrouvableDansImage'));
    }
  };

  // Toujours ouvrable : même sans caméra, l'import d'image et la saisie
  // manuelle restent des chemins valides.
  return {
    open: () => void openScanner(),
    dialog: (
      <Modal open={open} title={t('qr.scanner')} onClose={close}>
        <div className="stack">
          {info ? <Banner tone="warn">{info}</Banner> : null}

          {cameraOn ? (
            <>
              <video ref={videoRef} muted playsInline className="scanner-video" />
              <p className="muted">{t('qr.viserQr')}</p>
            </>
          ) : (
            <Button variant="secondary" onClick={() => void openScanner()}>
              <Camera size={16} aria-hidden="true" /> {t('qr.scannerCamera')}
            </Button>
          )}

          {/* Repli 3 : une simple photo du QR suffit */}
          <label className="btn btn--secondary">
            <ImageIcon size={16} aria-hidden="true" /> {t('qr.importerImage')}
            <input type="file" accept="image/*" className="sr-only"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void importImage(file);
              }} />
          </label>

          {/* Repli 4 : saisie manuelle, TOUJOURS disponible */}
          <hr className="divider" />
          <Field label={t('qr.codeOuLien')} hint={t('qr.saisirManuellement')}>
            <Input value={manual} onChange={setManual} />
          </Field>
          <Button disabled={!manual.trim()} onClick={() => accept(manual)}>
            <Keyboard size={16} aria-hidden="true" /> {t('qr.ouvrir')}
          </Button>
        </div>
      </Modal>
    ),
  };
}
